import { useEffect, useRef, useState } from 'react'
import { messageOf } from '../lib/errors'
import type { Me } from '../lib/me'
import { can, canGallery } from '../lib/me'
import { canReachShopAdmin } from '../lib/shop'
import { supabase } from '../lib/supabase'
import { myLanes } from '../lib/support'
import { AppShell } from '../components/AppShell'
import { Card } from '../components/Card'
import { SupportTabs } from '../components/AskOneDesk'
import {
  answersCsv,
  csvFileName,
  downloadText,
  loadCommsAnswers,
  loadCommsWeeks,
  type CommsAnswer,
  type CommsWeek,
} from '../lib/oneQuestionComms'

/**
 * `#/support/one-question` — One Question's answers, for Communications
 * (131, David 5 Oct).
 *
 * Employees answer once a week and can change their answer; they never see
 * anybody else's. This screen is where the answers go: every answer to a
 * week, named, with the person's business unit, department and legal entity,
 * and a CSV of the same rows — the hand-off into the One Voice artifact (a
 * word cloud, a map, whatever the week calls for). One Voice quotes WITHOUT
 * names; the card promised every answerer that before they typed, so the
 * lede repeats it here, where the names are.
 *
 * Readers are analytics.read (comms_admin, tenant_admin) — the Ask One
 * desk's readers, which is why it is a tab beside it. Both reads RAISE for
 * anybody else, and the refusal is rendered, not swallowed (rule 12).
 *
 * The live week is readable too: comms may watch it fill. The status shown is
 * the server's (`app.notice_status`); this screen never reads the clock.
 */
export function SupportOneQuestion({ me }: { me: Me }) {
  const reader = can(me, 'analytics.read')

  const [weeks, setWeeks] = useState<CommsWeek[] | null>(null)
  const [pick, setPick] = useState<string | null>(null)
  const [answers, setAnswers] = useState<CommsAnswer[] | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!reader) return
    let live = true
    loadCommsWeeks()
      .then((w) => {
        if (!live) return
        setWeeks(w)
        setPick((p) => p ?? w[0]?.contentId ?? null)
      })
      .catch((e) => live && setError(messageOf(e)))
    return () => {
      live = false
    }
  }, [reader])

  // Latest-wins: a slow read for the week you left must not land under the
  // week you picked (124's race).
  const seq = useRef(0)
  useEffect(() => {
    if (!reader || !pick) return
    const mine = ++seq.current
    setAnswers(null)
    loadCommsAnswers(pick)
      .then((a) => mine === seq.current && setAnswers(a))
      .catch((e) => mine === seq.current && setError(messageOf(e)))
  }, [reader, pick])

  const week = weeks?.find((w) => w.contentId === pick) ?? null

  const shell = (children: React.ReactNode) => (
    <AppShell
      current="One Question"
      canAdmin={can(me, 'admin.roles')}
      canShop={canReachShopAdmin(me)}
      canModerate={can(me, 'content.moderate')}
      canStarsAdmin={can(me, 'stars.admin')}
      canPhotoRoom={canGallery(me)}
      canAnalytics={reader}
      canNotices={can(me, 'content.publish')}
      canAudit={can(me, 'audit.read')}
      supportLanes={myLanes(me)}
      userName={me.displayName}
      userInitials={initialsOf(me.displayName)}
      userRole={me.roles[0]?.name ?? 'No role'}
      locale={me.locale}
      onSignOut={() => supabase.auth.signOut()}
    >
      {children}
    </AppShell>
  )

  if (!reader) {
    return shell(
      <p className="roles__error" role="alert">
        One Question answers are read by Communications only — it needs analytics.read (comms admins and the tenant
        admin). Nobody else sees anybody's answer.
      </p>,
    )
  }

  return shell(
    <>
      <SupportTabs on="one-question" deskLabel={myLanes(me).length ? 'Desk' : 'Your questions'} />
      <p className="supportinbox__lede">
        Everyone's answers to One Question, with their names. Only Communications sees this page — nobody else sees
        anybody's answer. People were told One Voice may quote them <strong>without names</strong>.
      </p>
      {error && (
        <p className="roles__error" role="alert">
          {error}
        </p>
      )}

      {weeks === null && !error && <p className="queue__empty">Loading…</p>}
      {weeks !== null && weeks.length === 0 && (
        <p className="queue__empty">No week has opened yet. Week 1 opens Monday 12 October at 00:01 Pacific.</p>
      )}

      {weeks !== null && weeks.length > 0 && (
        <Card>
          <div className="oqc__top">
            <label className="oqc__pick">
              Week
              <select value={pick ?? ''} onChange={(e) => setPick(e.target.value)}>
                {weeks.map((w) => (
                  <option key={w.contentId} value={w.contentId}>
                    {`Week ${w.seq} — ${w.opensAt.slice(0, 10)} — ${w.status === 'live' ? 'open now' : 'closed'} — ${w.answerCount} ${w.answerCount === 1 ? 'answer' : 'answers'}`}
                  </option>
                ))}
              </select>
            </label>
            <button
              type="button"
              className="btn btn--primary btn--sm"
              disabled={!week || !answers || answers.length === 0}
              onClick={() => week && answers && downloadText(csvFileName(week), answersCsv(week, answers))}
            >
              Download CSV
            </button>
          </div>

          {week && (
            <>
              <p className="oqc__question">{week.body}</p>
              <p className="oqc__meta">
                {week.status === 'live'
                  ? 'Open now — people can still answer and change their answers, so this list will move.'
                  : 'Closed.'}{' '}
                {answers ? `${answers.length} ${answers.length === 1 ? 'answer' : 'answers'}.` : ''}
              </p>
            </>
          )}

          {answers === null && pick && !error && <p className="queue__empty">Loading…</p>}
          {answers !== null && answers.length === 0 && <p className="queue__empty">Nobody has answered this one.</p>}
          {answers !== null && answers.length > 0 && (
            <table className="oqc__table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Business unit</th>
                  <th scope="col">Answer</th>
                  <th scope="col">Answered</th>
                </tr>
              </thead>
              <tbody>
                {answers.map((a) => (
                  <tr key={a.answerId}>
                    <td>{a.authorName}</td>
                    <td>{[a.businessUnit, a.department].filter(Boolean).join(' · ') || '—'}</td>
                    <td className="oqc__said">{a.body}</td>
                    <td className="oqc__when">
                      {a.answeredAt.slice(0, 10)}
                      {a.changedAt ? ' (changed)' : ''}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}
    </>,
  )
}

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((p) => p[0]?.toUpperCase() ?? '')
    .join('')
}
